import {
  Alert,
  Button,
  ButtonActionProps,
  FieldValues,
  FormControl,
  FormField,
  showNotification,
} from 'platform/components';
import {Box, Show, Space, Stack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useEffect, useState} from 'react';
import {Path, UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {useGetCebiaFeatureDecoderConfigQuery} from '@dms/api/core';
import {useLazyGetAutofillFeaturesQuery} from '@dms/api/sales';
import {SimilarVehicleResponseBody} from '@dms/api/shared';
import {useLazyVinDecoderDecodeQuery, VinDecoderResponseBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {AllOrNone, suffixTestId, TestIdProps} from 'shared/web';

import {validateVin} from 'features/vin-validate';

import {VehicleRecommendationType} from '../../types/VehicleRecommendationType';
import {handleApiError} from '../../utils/handleApiError';
import {SimilarVehicles} from './components/SimilarVehicles';
import {VehicleSelectionType} from './components/VehiclePreview';
import {filterKeys} from './utils/filterKeys';
import {filterRegistrationPlateKeys} from './utils/filterRegistrationPlateKeys';
import {transformPaste} from './utils/transformPaste';
import {transformRegistrationPlatePaste} from './utils/transformRegistrationPlatePaste';

type SimilarVehiclesDisplayProps = AllOrNone<{
  shouldRecommendVehiclesFor?: VehicleRecommendationType;
  onVehicleSelect: (
    vehicle: SimilarVehicleResponseBody,
    selectionType: VehicleSelectionType
  ) => void;
}> & {
  hideChooseButton?: boolean;
  excludeFromResultVehicleIds?: string[];
  additionalActions?: (vehicle: SimilarVehicleResponseBody) => ButtonActionProps[];
};

export type VinDecoderFieldProps<TFieldValues extends FieldValues = FieldValues> = {
  control: FormControl<TFieldValues>;
  formApi: UseFormReturn<TFieldValues>;
  vinField: {
    name: Path<TFieldValues>;
    label?: string;
    isDisabled?: boolean;
    isRecommended?: boolean;
    onVinDecode?: (vin: string) => void;
    onChange?: (value: string | null) => void;
  };
  registrationPlateField: {
    name: Path<TFieldValues>;
    label?: string;
    isDisabled?: boolean;
    isRecommended?: boolean;
  };
  isStatic?: boolean;
  onDecodedData?: (decodedData?: VinDecoderResponseBody) => void;
  isCebiaFeatureDecoderEnabled?: boolean;
  isSaving?: boolean;
} & TestIdProps &
  SimilarVehiclesDisplayProps;

export function VinDecoderField<TFieldValues extends FieldValues = FieldValues>(
  props: VinDecoderFieldProps<TFieldValues>
) {
  const [decodeVin] = useLazyVinDecoderDecodeQuery();
  const {data: {enabled: isCebiaFeatureDecoderEnabled} = {}} = useGetCebiaFeatureDecoderConfigQuery(
    undefined,
    {skip: !props.isCebiaFeatureDecoderEnabled}
  );
  const [getCebiaAutofillFeatures] = useLazyGetAutofillFeaturesQuery();

  const [isDecoding, setIsDecoding] = useState(false);
  const [decodeStatus, setDecodeStatus] = useState<'full' | 'partial' | 'delayed' | 'error' | null>(
    null
  );
  const [delayedDecoding, setDelayedDecoding] = useState<{
    isDelayed: boolean;
    retries: number;
    vin: string | null;
  }>({isDelayed: false, retries: 0, vin: null});

  const mapDecodingStatusToAlert = (decodingStatus?: VinDecoderResponseBody['decodingStatus']) =>
    match<VinDecoderResponseBody['decodingStatus'], 'full' | 'partial' | 'delayed' | 'error'>(
      decodingStatus
    )
      .with('fully', () => 'full')
      .with('partially', () => 'partial')
      .with('delayed', () => 'delayed')
      .otherwise(() => 'error');

  const runDecode = useCallback(
    async (vin: string, isInitialAttempt: boolean = true) => {
      setIsDecoding(true);
      const decodedData = await decodeVin({vin}).unwrap().catch(handleApiError);

      if (decodedData) {
        const statusVariant = mapDecodingStatusToAlert(decodedData.decodingStatus);
        setDecodeStatus(statusVariant);
      }

      if (decodedData?.decodingStatus === 'delayed') {
        setDelayedDecoding((prev) => ({
          isDelayed: true,
          retries: prev.isDelayed ? prev.retries + 1 : 0,
          vin,
        }));
      } else {
        setDelayedDecoding({isDelayed: false, retries: 0, vin: null});
      }

      if (isCebiaFeatureDecoderEnabled && isInitialAttempt) {
        getCebiaAutofillFeatures({vin})
          .unwrap()
          .then((response) => {
            showNotification.success(i18n.t('entity.vehicle.notifications.featuresLoaded'));
            props.onDecodedData?.({...(decodedData ?? {}), features: response.features});
          })
          .catch(handleApiError)
          .finally(() => setIsDecoding(false));
      } else {
        props.onDecodedData?.(decodedData ?? undefined);
        setIsDecoding(false);
      }
    },
    [decodeVin, getCebiaAutofillFeatures, isCebiaFeatureDecoderEnabled, props]
  );

  const handleDecodeVin = async () => {
    if (props.isStatic) {
      return;
    }

    const vin = props.formApi.getValues(props.vinField.name);
    props.vinField?.onVinDecode?.(vin);

    if (isNil(vin)) {
      return props.formApi.setError(props.vinField.name, {
        message: i18n.t('entity.vehicle.notifications.vinIsRequired'),
      });
    }

    if (!validateVin(vin)) {
      return props.formApi.setError(props.vinField.name, {
        message: i18n.t('entity.vehicle.notifications.vinIsInvalid'),
      });
    }

    try {
      await runDecode(vin, true);
    } catch {
      setDelayedDecoding({isDelayed: false, retries: 0, vin: null});
      setDecodeStatus('error');
    }
  };

  useEffect(() => {
    if (!delayedDecoding.isDelayed) {
      return;
    }

    if (delayedDecoding.retries >= 2) {
      // After 3 total attempts (initial + 2 retries), fail
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDelayedDecoding({isDelayed: false, retries: 0, vin: null});
      setDecodeStatus('error');
      return;
    }

    const timeoutId = setTimeout(() => {
      if (delayedDecoding.vin) {
        runDecode(delayedDecoding.vin, false);
      }
    }, 60000);

    return () => clearTimeout(timeoutId);
  }, [delayedDecoding, runDecode]);

  return (
    <VStack spacing={4}>
      <Stack direction={['column', 'column', 'row', 'row']} spacing={[6, 6, 4, 4]}>
        <FormField
          control={props.control}
          type="text"
          name={props.registrationPlateField.name}
          label={props.registrationPlateField.label}
          data-testid={suffixTestId('registrationPlate', props)}
          isRecommended={props.registrationPlateField.isRecommended}
          isDisabled={props.registrationPlateField.isDisabled}
          onKeyDown={filterRegistrationPlateKeys}
          onPaste={transformRegistrationPlatePaste}
        />
        <Box flex={1}>
          <FormField
            control={props.control}
            type="text"
            maxLength={17}
            isCounterVisible
            name={props.vinField.name}
            label={props.vinField.label}
            isRecommended={props.vinField.isRecommended}
            onChange={(value) => {
              props.formApi.clearErrors(props.vinField.name);
              props.vinField.onChange?.(value);
            }}
            onKeyDown={filterKeys}
            onPaste={transformPaste}
            isDisabled={props.vinField.isDisabled}
            data-testid={suffixTestId('vin', props)}
          />
        </Box>
        <Show when={!props.isStatic}>
          <VStack>
            <Show onDesktop onNotebook>
              <Space vertical={5} />
            </Show>
            <Button
              title={i18n.t('general.labels.decode')}
              onClick={handleDecodeVin}
              isDisabled={props.vinField.isDisabled}
              isLoading={props.isSaving || isDecoding || delayedDecoding.isDelayed}
              data-testid={suffixTestId('decode', props)}
            />
          </VStack>
        </Show>
      </Stack>
      {isNotNil(props.shouldRecommendVehiclesFor) && isNotNil(props.onVehicleSelect) && (
        <SimilarVehicles
          control={props.control}
          registrationPlateField={props.registrationPlateField}
          shouldRecommendVehiclesFor={props.shouldRecommendVehiclesFor}
          vinField={props.vinField}
          onVehicleSelect={props.onVehicleSelect}
          hideChooseButton={props.hideChooseButton}
          excludeFromResultVehicleIds={props.excludeFromResultVehicleIds}
          additionalActions={props.additionalActions}
          data-testid={suffixTestId('SimilarVehicles', props)}
        />
      )}
      <Show when={decodeStatus === 'full'}>
        <Alert
          variant="success"
          title={i18n.t('entity.vehicle.notifications.vinDecodedSuccessfully')}
          message={i18n.t('entity.vehicle.notifications.vinDecodedSuccessfullyDescription')}
          data-testid={suffixTestId('vinDecodedSuccessfully', props)}
        />
      </Show>
      <Show when={decodeStatus === 'partial'}>
        <Alert
          variant="warning"
          title={i18n.t('entity.vehicle.notifications.vinDecodedPartially')}
          message={i18n.t('entity.vehicle.notifications.vinDecodedPartiallyDescription')}
          data-testid={suffixTestId('vinDecodedPartially', props)}
        />
      </Show>
      <Show when={decodeStatus === 'delayed'}>
        <Alert
          variant="warning"
          title={i18n.t('entity.vehicle.notifications.vinDecodedDelayed')}
          message={i18n.t('entity.vehicle.notifications.vinDecodedDelayedDescription')}
          data-testid={suffixTestId('vinDecodedDelayed', props)}
        />
      </Show>
      <Show when={decodeStatus === 'error'}>
        <Alert
          variant="error"
          title={i18n.t('entity.vehicle.notifications.vinDecodedError')}
          message={i18n.t('entity.vehicle.notifications.vinDecodedErrorDescriptions')}
          data-testid={suffixTestId('vinDecodedError', props)}
        />
      </Show>
    </VStack>
  );
}
